import compressor from 'browser-image-compression';
import { doc, getDoc, setDoc, updateDoc, writeBatch } from 'firebase/firestore';
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useHistory, useLocation, useParams } from 'react-router-dom';
import { v4 } from 'uuid';
import * as Yup from 'yup';
import { useAuth } from '~/hooks/auth';
import { DB } from '~/services/firebase';

import AWSBucket, { AWSParams } from '~/services/aws';

import Dashboard from '~/layouts/Dashboard';
import getValidationErrors from '~/utils/getValidationErrors';
// import ICompany from '~/interfaces/ICompany';
import { useCategoriesCompany } from '~/hooks/categoriesCompany';
import { useProductsCompany } from '~/hooks/productsCompany';
import { useToast } from '~/hooks/toast';
import ICategory from '~/interfaces/ICategory';
import { bff } from '~/services/bff';

interface FormData {
  uid: string;
  name: string;
  image: string;
  order: number;
  // image: File;
  status: boolean;
}

interface Params {
  id: string;
}

const { REACT_APP_AWS_BUCKET_URL, REACT_APP_FIREBASE_PROJECT } = process.env;

const CategoryForm: React.FC = () => {
  const history = useHistory();
  const params = useParams<Params>();
  const { state } = useLocation();
  const { user, company } = useAuth();
  const { addToast } = useToast();
  const { updateCategories } = useCategoriesCompany();
  const { products } = useProductsCompany();

  const [data, setData] = useState({} as FormData);
  const [errors, setErrors] = useState({} as any);
  const [loading, setLoading] = useState(false);
  const { id } = params;

  useEffect(() => {
    async function loadCategory() {
      const categoriesRef = doc(DB, 'categories', id);
      const docSnap = await getDoc(categoriesRef);
      setData({
        ...docSnap.data(),
        uid: id,
      } as FormData);
    }

    if (state) {
      setData(state as FormData);
    } else if (id) {
      loadCategory();
    } else {
      setData({ status: true } as FormData);
    }
  }, [id, state]);

  const updateCategoryInProducts = useCallback(
    async (category: ICategory) => {
      if (id) {
        const batch = writeBatch(DB);

        products.forEach(product => {
          if (product.category_id === id) {
            const productRef = doc(DB, 'products', product.uid);
            batch.update(productRef, { category });
          }
        });

        await batch.commit();
      }
    },
    [id, products],
  );

  const handleSubmit = useCallback(
    async event => {
      event.preventDefault();

      try {
        const schema = Yup.object().shape({
          name: Yup.string().required('O nome da categotia é obrigatória'),
        });

        await schema.validate(data, {
          abortEarly: false,
        });

        setLoading(true);

        const { name, status, order } = data;

        const inputFile = document.getElementById('photo') as HTMLInputElement;

        let image = '';

        // OLD_FIREBASE
        // if (inputFile && inputFile.files && inputFile.files.length > 0) {
        //   const file = inputFile.files[0];
        //   const storageRef = Storage.ref(`categories/${v4()}.png`);
        //   const resposne = await storageRef.put(file);
        //   image = await resposne.ref.getDownloadURL();
        // }

        if (inputFile && inputFile.files && inputFile.files.length > 0) {
          const file = inputFile.files[0];
          const filename = `${company.fantasy.trim()}-${
            company.uid
          }/${v4()}.png`;

          const compressedFile = await compressor(file, {
            maxSizeMB: 1,
            maxWidthOrHeight: 100,
            useWebWorker: true,
          });

          const config: any = {
            ...AWSParams,
            Body: compressedFile,
            Key: filename,
            // ACL não é necessário com "Bucket owner enforced" - use bucket policy
          };

          AWSBucket.putObject(config).send();

          // if (data.uid && data.image) {
          //   const Key = data.image.replace(`${REACT_APP_AWS_BUCKET_URL}/`, '');
          //   config = {
          //     ...AWSParams,
          //     Key,
          //   };

          //   AWSBucket.deleteObject(config, (err, tmp) => {
          //     if (err) console.log(err);
          //   });
          // }

          image = `${REACT_APP_AWS_BUCKET_URL}/${filename}`;
        }

        const uid = data.uid || v4();

        let parseData: any = {
          ...data,
          uid,
          name,
          user_id: user.uid,
          company_id: company.uid,
          status,
          order,
        };

        if (image) parseData = { ...parseData, image };

        if (data.uid) {
          await updateDoc(doc(DB, 'categories', uid), parseData);
          await updateCategoryInProducts(parseData);
          updateCategories(parseData);
        } else {
          await setDoc(doc(DB, 'categories', uid), parseData);

          updateCategories(parseData, true);
        }

        await bff.delete(`/categories/${REACT_APP_FIREBASE_PROJECT}`);
        await bff.delete(`/products/${REACT_APP_FIREBASE_PROJECT}`);

        addToast({
          type: 'success',
          description: data.uid
            ? 'Categoria alterada com sucesso!'
            : 'Categoria criada com sucesso!',
        });

        history.push('/categories');

        setLoading(false);
      } catch (err: any) {
        console.log(err);
        setLoading(false);
        if (err instanceof Yup.ValidationError) {
          const parseErrors = getValidationErrors(err);
          setErrors(parseErrors);
        }
      }
    },
    [
      user,
      company,
      data,
      addToast,
      history,
      updateCategories,
      updateCategoryInProducts,
    ],
  );

  return (
    <Dashboard containerMessage="col-xl-6 offset-xl-3 col-md-10 offset-md-1">
      <div className="row">
        <div className="col-xl-6 offset-xl-3 col-md-10 offset-md-1">
          <div className="nk-block nk-block-lg">
            <div className="nk-block-head">
              <div className="nk-block-head-content">
                <div className="nk-block-head-sub">
                  <Link className="back-to" to="/categories">
                    <em className="icon ni ni-arrow-left" />
                    <span>Voltar</span>
                  </Link>
                </div>
                <h4 className="title nk-block-title">
                  {id ? 'Editar Categoria' : 'Criar Categoria'}
                </h4>
                <div className="nk-block-des">
                  <p>Preencha o formulário abaixo e crie uma nova categoria.</p>
                </div>
              </div>
            </div>
            <div className="card card-bordered">
              <div className="card-inner">
                <form onSubmit={handleSubmit}>
                  <div className="row g-2">
                    <div className="col-lg-6">
                      <div className="form-group">
                        <label className="form-label">Nome da Categoria</label>
                        <div className="form-control-wrap">
                          <input
                            type="text"
                            className="form-control"
                            defaultValue={data.name}
                            onChange={event => {
                              setData({ ...data, name: event.target.value });
                            }}
                          />
                          {errors.name && (
                            <span className="invalid">{errors.name}</span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="col-lg-6">
                      <div className="form-group">
                        <label className="form-label">Ordem</label>
                        <div className="form-control-wrap">
                          <input
                            type="number"
                            step="1"
                            className="form-control"
                            defaultValue={data.order}
                            onChange={event => {
                              setData({
                                ...data,
                                order: parseInt(event.target.value),
                              });
                            }}
                          />
                          {errors.order && (
                            <span className="invalid">{errors.order}</span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* <div className="col-lg-6">
                      <div className="form-group">
                        <label className="form-label">Link da Imagem</label>
                        <div className="form-control-wrap">
                          <input
                            type="text"
                            className="form-control"
                            defaultValue={data.image}
                            onChange={event => {
                              setData({ ...data, image: event.target.value });
                            }}
                          />
                          {errors.image && (
                            <span className="invalid">{errors.image}</span>
                          )}
                        </div>
                      </div>
                    </div> */}
                    <div className="col-lg-6">
                      <div className="form-group">
                        <label className="form-label">Imagem</label>
                        <div className="form-control-wrap">
                          <input
                            type="file"
                            id="photo"
                            style={{ width: '100%' }}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="col-lg-6">
                      <div className="form-group">
                        <label className="form-label">
                          Status da categoria?
                        </label>
                        <ul className="custom-control-group g-3 align-center">
                          <li>
                            <div className="custom-control custom-control-sm custom-checkbox">
                              <input
                                id="active"
                                type="checkbox"
                                className="custom-control-input"
                                defaultChecked={data.status}
                                onClick={() => {
                                  setData({
                                    ...data,
                                    status: !data.status,
                                  });
                                }}
                              />
                              <label
                                className="custom-control-label"
                                htmlFor="active"
                              >
                                Ativo
                              </label>
                            </div>
                          </li>
                        </ul>
                      </div>
                    </div>

                    <div className="col-12">
                      <div className="form-group">
                        <button
                          type="submit"
                          className="btn btn-lg btn-primary"
                          disabled={loading}
                        >
                          {loading && (
                            <div
                              className="spinner-border spinner-border-sm"
                              role="status"
                            />
                          )}
                          {!loading && 'Salvar'}
                        </button>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Dashboard>
  );
};

export default CategoryForm;
